import { useAuth } from '../contexts/AuthContext';
import type { PlanType } from '../../shared/types';
import NotificationBell from './NotificationBell';
import type { AppNotification } from '../hooks/useAnalysisJobs';

interface HeaderProps {
  onMenuToggle?: () => void;
  /** Nombre d'analyses en cours / en attente — alimente le pill et la cloche. */
  runningCount?: number;
  queuedCount?: number;
  /** Clic sur le pill — repositionne l'app sur l'analyse en cours la plus récente. */
  onResumeLoader?: () => void;
  /** Centre de notifications. */
  notifications?: AppNotification[];
  unreadCount?: number;
  onOpenNotification?: (notif: AppNotification) => void;
  onMarkAllNotificationsRead?: () => void;
  onDismissNotification?: (id: string) => void;
}

const PREVIEW_OPTIONS: { value: PlanType | 'admin' | 'onboarding' | 'bundle'; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'onboarding', label: 'Signup' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'beta', label: 'Beta' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
  { value: 'partner', label: 'Partenaire' },
];

export default function Header({
  onMenuToggle,
  runningCount = 0,
  queuedCount = 0,
  onResumeLoader,
  notifications = [],
  unreadCount = 0,
  onOpenNotification,
  onMarkAllNotificationsRead,
  onDismissNotification,
}: HeaderProps) {
  const { user, previewPlan, setPreviewPlan, previewOnboarding, setPreviewOnboarding, previewBundle, setPreviewBundle, isOnboarding, signOut } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true;
  const inFlight = runningCount + queuedCount;
  const identity = user?.displayName || user?.email || '';

  return (
    <header className="sticky top-0 z-50 shrink-0">
      <div className="flex h-16 items-center justify-between gap-3 px-4 md:px-6">
        {/* Gauche : hamburger mobile + badge bêta */}
        <div className="flex min-w-0 items-center gap-2">
          {onMenuToggle && !isOnboarding && (
            <button
              onClick={onMenuToggle}
              className="grid h-9 w-9 place-items-center rounded-full bg-ink text-white transition-transform hover:scale-105 active:scale-95 md:hidden"
              aria-label="Ouvrir le menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="h-[18px] w-[18px]">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <span
            className="shrink-0 cursor-default rounded-full bg-sun-400 px-3 py-1.5 text-xs font-medium text-ink"
            title="Version bêta — limitée à 100 places"
          >
            Bêta · 100 places
          </span>
        </div>

        {/* Centre : pilule « analyse en cours » — ramène sur l'analyse active. */}
        {!isOnboarding && inFlight > 0 && (
          <button
            onClick={onResumeLoader}
            className="flex items-center gap-2 rounded-full bg-rose-pastel-300 px-3.5 py-1.5 text-xs font-medium text-ink transition-transform hover:scale-[1.03]"
            title="Cliquez pour revenir à l'analyse en cours"
            aria-label={`${inFlight} analyse(s) en cours`}
          >
            <svg className="h-3.5 w-3.5 shrink-0 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
              <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
            </svg>
            <span className="whitespace-nowrap">
              {inFlight > 1 ? `${inFlight} analyses en cours` : 'Analyse en cours'}
            </span>
          </button>
        )}

        {/* Right: preview toggle + badge + theme */}
        <div className="flex items-center gap-2">
          {/* Super admin plan preview toggle */}
          {isSuperAdmin && (
            <div className="hidden items-center gap-1 rounded-full bg-sun-100 px-1 py-0.5 sm:flex">
              {PREVIEW_OPTIONS.map(opt => {
                const isActive = opt.value === 'admin'
                  ? previewPlan === null && !previewOnboarding && !previewBundle
                  : opt.value === 'onboarding'
                    ? previewOnboarding
                    : opt.value === 'bundle'
                      ? previewBundle
                      : previewPlan === opt.value && !previewOnboarding && !previewBundle;
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (opt.value === 'admin') {
                        setPreviewPlan(null);
                        setPreviewOnboarding(false);
                        setPreviewBundle(false);
                      } else if (opt.value === 'onboarding') {
                        setPreviewOnboarding(true);
                      } else if (opt.value === 'bundle') {
                        setPreviewBundle(true);
                      } else {
                        setPreviewPlan(opt.value as PlanType);
                      }
                    }}
                    className={`rounded-full px-2 py-1 text-[10px] font-medium transition-colors ${
                      isActive
                        ? 'bg-ink text-white'
                        : 'text-sun-700 hover:bg-sun-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Onboarding : pas de cloche, juste identité + déconnexion (focus sur le centre). */}
          {isOnboarding ? (
            <>
              <span className="hidden max-w-[180px] truncate text-xs text-slate-500 sm:block" title={identity}>
                {identity}
              </span>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-2 text-xs font-medium text-white transition-transform hover:scale-[1.03]"
                title="Se déconnecter"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="hidden sm:inline">Se déconnecter</span>
              </button>
            </>
          ) : (
            <NotificationBell
              notifications={notifications}
              unreadCount={unreadCount}
              runningCount={runningCount}
              queuedCount={queuedCount}
              onOpen={(n) => onOpenNotification?.(n)}
              onMarkAllRead={() => onMarkAllNotificationsRead?.()}
              onDismiss={(id) => onDismissNotification?.(id)}
            />
          )}

        </div>
      </div>
    </header>
  );
}
